export { default as Address } from "./Address";
export { default as ArthrosamidLots } from "./ArthrosamidLots";
export { default as AddButton } from "./AddButton";
export { default as BackButton } from "./BackButton";
export { default as BaseActionButton } from "./BaseActionButton";
export { KeepDialogOpenOnSaveContext, useKeepDialogOpenOnSave } from "./KeepDialogOpenOnSave";
export { default as CancelButton } from "./CancelButton";
export { default as ConfirmButton } from "./ConfirmButton";
export { default as ContinueButton } from "./ContinueButton";
export { default as DeleteButton } from "./DeleteButton";
export { default as DoneButton } from "./DoneButton";
export { default as DownloadButton } from "./DownloadButton";
export { default as EditButton } from "./EditButton";
export { default as Checkbox } from "./Checkbox";
export { default as CheckboxList } from "./CheckboxList";
export { default as ComboBox } from "./ComboBox";
export { default as Date } from "./Date";
export { default as DateInput } from "./DateInput";
export { DEFAULT_DATETIME_TIME } from "./dateTimeDefaults";
export { default as DateTime } from "./DateTime";
export { default as DateTimeForm } from "./DateTimeForm";
export { default as DateTimePicker } from "./DateTimePicker";
export { default as Email } from "./Email";
export { default as Errors } from "./Errors";
export { default as FileInput } from "./FileInput";
export { default as FirstName } from "./FirstName";
export { default as FormErrors } from "./FormErrors";
export * from "./FormSelectDialog";
export * from "./ownedBuilderDraft";
export { default as GroupedFields } from "./GroupedFields";
export { default as Input } from "./Input";
export { default as LastName } from "./LastName";
export { default as MonthYear } from "./MonthYear";
export { default as MultipleChoiceCards } from "./MultipleChoiceCards";
export { default as MultiSelect } from "./MultiSelect";
export { default as NextButton } from "./NextButton";
export { default as Number } from "./Number";
export { default as NumberFlow } from "./NumberFlow";
export { default as Password } from "./Password";
export { default as Phone } from "./Phone";
export { default as PIFU } from "./PIFU";
export { default as Rating } from "./Rating";
export { default as RatingButton } from "./RatingButton";
export { default as RatingOptions } from "./RatingOptions";
export { default as Recipient } from "./Recipient";
export { default as Recipients } from "./Recipients";
export { default as RetryButton } from "./RetryButton";
export { default as SaveButton } from "./SaveButton";
export { default as SendButton } from "./SendButton";
export { default as SkipButton } from "./SkipButton";
export { default as StopButton } from "./StopButton";
export { default as SubmitButton } from "./SubmitButton";
export * from "./formatScheduleTimepointsList";
export * from "./ScheduleSelectDialog";
export { default as Select, type OptionT, arrayToOptions } from "./Select";
export { default as SelectRecipient } from "./SelectRecipient";
export { default as SelectTimeUnit } from "./SelectTimeUnit";
export { default as SelectTitle } from "./SelectTitle";
export { default as SingleChoice } from "./SingleChoice";
export { default as Star } from "./Star";
export { default as Switch } from "./Switch";
export { default as Text } from "./Text";
export { default as Textarea } from "./Textarea";
export { default as TextForm } from "./TextForm";
export { default as Time } from "./Time";
export { default as UserCombobox } from "./UserCombobox";
export { default as YesNo } from "./YesNo";
export { getOptionTestId, getTestId } from "./utils/getTestId";

// Export new form attribute components
export { default as IconDetail } from "./components/IconDetail";
export { FormAttributeBadges } from "./components/FormAttributeBadges";
export { FormMetrics } from "./components/FormMetrics";
export { ClinicalInformationCard } from "./components/ClinicalInformationCard";
export { FormInstructionsCard } from "./components/FormInstructionsCard";
export { FormResultsBody, FormResultsCard } from "./components/FormResultsCard";
export { CTACard } from "./components/CTACard";
export { BenefitsSection } from "./components/BenefitsSection";
export { EmailResultsDialog } from "./components/EmailResultsDialog";
export { EmailGateDialog } from "./components/EmailGateDialog";
export { PlanCard, PlanActions } from "./components/PlanCard";
export { MarkdownFormatHint, RichLabel } from "./components/RichLabel";
export { RichText } from "./components/RichText";
export { ChoiceGroup, type ChoiceGroupOption } from "./components/ChoiceGroup";
export { ChoiceCard } from "./components/ChoiceCard";
export {
    OWN_OPTION_MAX_LENGTH,
    OWN_OPTIONS_PER_QUESTION,
    OwnOptionInUseError,
    useOwnOptionRows,
    type AddOwnOptionProps,
    type OwnOptionCopy,
} from "./components/OwnOptionControls";
export { type FieldOwnOptions } from "./components/OwnOptionField";
export { FormDisplayProvider, OptionalMark } from "./components/FormDisplayContext";
export {
    PictureCountGroup,
    PictureCountInput,
    type PictureCountInputProps,
    type PictureCountItem,
} from "./components/PictureCountGroup";
export { scaleOptionAnswerText } from "./utils/scaleOptionAnswerText";
export { generateResultsEmail } from "./utils/generateResultsEmail";
export { default as FormPreviewSwitch } from "./previews/FormPreviewSwitch";
export { PdfInputPreview, PdfScaleBoxRow, PdfVisaQ8 } from "./pdf-previews";
export type { ScaleOption } from "./pdf-previews";

// Export hooks
export { useMediaQuery } from "./hooks/useMediaQuery";

// Export services
export { capitaliseSentence } from "./services/capitaliseSentence";
export { deSnakeCase } from "./services/deSnakeCase";
export * from "./pageLogic";
export * from "./pageValidation";
export * from "./screeners";

// Legacy formComponents export for backward compatibility
import Address from "./Address";
import AddButton from "./AddButton";
import BackButton from "./BackButton";
import BaseActionButton from "./BaseActionButton";
import CancelButton from "./CancelButton";
import ConfirmButton from "./ConfirmButton";
import ContinueButton from "./ContinueButton";
import DeleteButton from "./DeleteButton";
import DoneButton from "./DoneButton";
import DownloadButton from "./DownloadButton";
import EditButton from "./EditButton";
import Checkbox from "./Checkbox";
import CheckboxList from "./CheckboxList";
import ComboBox from "./ComboBox";
import DateInput from "./Date";
import DateTime from "./DateTime";
import DateTimeForm from "./DateTimeForm";
import DateTimePicker from "./DateTimePicker";
import Email from "./Email";
import Errors from "./Errors";
import FirstName from "./FirstName";
import FormErrors from "./FormErrors";
import GroupedFields from "./GroupedFields";
import Input from "./Input";
import LastName from "./LastName";
import MonthYear from "./MonthYear";
import MultipleChoiceCards from "./MultipleChoiceCards";
import MultiSelect from "./MultiSelect";
import NextButton from "./NextButton";
import NumberInput from "./Number";
import NumberFlow from "./NumberFlow";
import Password from "./Password";
import Phone from "./Phone";
import PIFU from "./PIFU";
import Rating from "./Rating";
import RatingButton from "./RatingButton";
import RatingOptions from "./RatingOptions";
import Recipient from "./Recipient";
import Recipients from "./Recipients";
import RetryButton from "./RetryButton";
import SaveButton from "./SaveButton";
import SendButton from "./SendButton";
import SkipButton from "./SkipButton";
import StopButton from "./StopButton";
import SubmitButton from "./SubmitButton";
import Select from "./Select";
import SelectRecipient from "./SelectRecipient";
import SelectTimeUnit from "./SelectTimeUnit";
import SelectTitle from "./SelectTitle";
import SingleChoice from "./SingleChoice";
import Star from "./Star";
import Switch from "./Switch";
import Text from "./Text";
import TextForm from "./TextForm";
import Textarea from "./Textarea";
import Time from "./Time";
import ProfileCombobox from "./UserCombobox";
import YesNo from "./YesNo";

export const formComponents = {
    Address,
    AddButton,
    BackButton,
    BaseActionButton,
    CancelButton,
    ConfirmButton,
    ContinueButton,
    DeleteButton,
    DoneButton,
    DownloadButton,
    EditButton,
    Checkbox,
    CheckboxList,
    ComboBox,
    Date: DateInput,
    DateTime,
    DateTimeForm,
    DateTimePicker,
    Email,
    Errors,
    FirstName,
    FormErrors,
    GroupedFields,
    Input,
    LastName,
    MonthYear,
    MultipleChoiceCards,
    MultiSelect,
    NextButton,
    Number: NumberInput,
    NumberFlow,
    Password,
    Phone,
    PIFU,
    Rating,
    RatingButton,
    RatingOptions,
    Recipient,
    Recipients,
    RetryButton,
    SaveButton,
    SendButton,
    SkipButton,
    StopButton,
    SubmitButton,
    Select,
    SelectRecipient,
    SelectTimeUnit,
    SelectTitle,
    SingleChoice,
    Star,
    Switch,
    Text,
    TextForm,
    Textarea,
    Time,
    UserCombobox: ProfileCombobox,
    YesNo,
};
